import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import {
  createRiskAssessmentAsync,
  emailRecommendedProductAsync,
  getRiskAssessmentAsync,
  getRiskAssessmentByIdAsync,
  saveRiskAssessmentAsync,
} from "../../../../../app/homeSlice";
import { useLoading } from "../../../../../hooks/useLoading";
import { LOADING_KEYS } from "../../../../../constants/loadingKeys";
import { SUCCESS_STATUS } from "../../../../../constants";
import { FIELD_DEFINITIONS, INITIAL_FORM_DATA, SUCCESS_MESSAGE_DURATION_MS, VIEW } from "../constant/RiskAssessment.constant";
import { buildBradenPayload, buildFieldsWithOptions, buildSavePayload, mapAssessmentToFormData, validateFields } from "../utils/riskAssessment.utils";
import { getRiskAssessmentOptionsAsync } from "../../../../../app/patientSlice";

export const useRiskAssessment = () => {
  const dispatch = useDispatch();

  const formularyId = useSelector((s) => s.user?.formularyId);
  const bradenOptions = useSelector(
    (s) => ({
      bradenSensoryPerception: s.patient.bradenSensoryPerception,
      bradenMoisture: s.patient.bradenMoisture,
      bradenActivity: s.patient.bradenActivity,
      bradenMobility: s.patient.bradenMobility,
      bradenNutrition: s.patient.bradenNutrition,
      bradenFrictionShear: s.patient.bradenFrictionShear,
    }),
    shallowEqual
  );

  const riskAssessmentList = useSelector((s) => s.home?.riskassessments);
  const preselectedDetail = useSelector((s) => s.home?.selectedRiskAssessment);

  const createRiskLoading = useLoading(LOADING_KEYS.CREATE_RISK_ASSESSMENT);
  const saveAssessmentLoading = useLoading(LOADING_KEYS.SAVE_RISK_ASSESSMENT);
  const emailLoading = useLoading(LOADING_KEYS.EMAIL_RECOMMENDED_PRODUCT);
  const detailLoading = useLoading(LOADING_KEYS.GET_RISK_ASSESSMENT_BY_ID);
  const riskLoading = useLoading(LOADING_KEYS.GET_RISK_ASSESSMENT);

  const fields = useMemo(() => buildFieldsWithOptions(FIELD_DEFINITIONS, bradenOptions), [bradenOptions]);

  const [view, setView] = useState(VIEW.FORM);
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [errors, setErrors] = useState({});
  const [riskResult, setRiskResult] = useState({});
  const [assessmentName, setAssessmentName] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [showNameInput, setShowNameInput] = useState(false);
  const [pendingName, setPendingName] = useState("");

  const hasManualSelectionRef = useRef(false);

  useEffect(() => {
    if (!successMessage) return undefined;
    const timer = setTimeout(() => setSuccessMessage(null), SUCCESS_MESSAGE_DURATION_MS);
    return () => clearTimeout(timer);
  }, [successMessage]);

  useEffect(() => {
    dispatch(getRiskAssessmentAsync());
  }, [dispatch]);

  useEffect(() => {
    dispatch(getRiskAssessmentOptionsAsync("bradensensoryperception"));
    dispatch(getRiskAssessmentOptionsAsync("bradenmoisture"));
    dispatch(getRiskAssessmentOptionsAsync("bradenactivity"));
    dispatch(getRiskAssessmentOptionsAsync("bradenmobility"));
    dispatch(getRiskAssessmentOptionsAsync("bradennutrition"));
    dispatch(getRiskAssessmentOptionsAsync("bradenfrictionshear"));
  }, [dispatch]);

  useEffect(() => {
    if (hasManualSelectionRef.current) return;
    if (Array.isArray(riskAssessmentList) && riskAssessmentList.length > 0) {
      setSelectedId(riskAssessmentList[0]?.id);
    }
  }, [riskAssessmentList]);

  useEffect(() => {
    if (hasManualSelectionRef.current) return;
    if (!preselectedDetail) return;
    setRiskResult(preselectedDetail);
    setFormData(mapAssessmentToFormData(preselectedDetail));
    setAssessmentName(preselectedDetail?.assessmentName);
    setErrors({});
    setShowNameInput(false);
    setPendingName("");
    setView(VIEW.DETAIL);
  }, [preselectedDetail]);

  const resetWorkspace = useCallback(() => {
    setFormData(INITIAL_FORM_DATA);
    setErrors({});
    setRiskResult({});
    setAssessmentName("");
    setSelectedId(null);
    setShowNameInput(false);
    setPendingName("");
    setView(VIEW.FORM);
  }, []);

  const handleCreate = useCallback(() => {
    setFormData(INITIAL_FORM_DATA);
    setErrors({});
    setRiskResult({});
    setAssessmentName("");
    setSelectedId(null);
    setShowNameInput(false);
    setPendingName("");
    setView(VIEW.FORM);
  }, []);

  const handleChipChange = useCallback((name, key) => {
    setFormData((prev) => ({ ...prev, [name]: key }));
    setErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  }, []);

  const handleCancelForm = useCallback(() => {
    if (selectedId) {
      setErrors({});
      setShowNameInput(false);
      setPendingName("");
      setView(VIEW.DETAIL);
    } else {
      resetWorkspace();
    }
  }, [resetWorkspace, selectedId]);

  const onSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      const validationErrors = validateFields(FIELD_DEFINITIONS, formData);

      if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);
        return;
      }

      setErrors({});

      const payload = buildBradenPayload(formData);
      const result = await dispatch(createRiskAssessmentAsync(payload, selectedId ?? 0));

      if (result) {
        setRiskResult(result);
        setView(VIEW.DETAIL);
      }
    },
    [dispatch, formData, selectedId]
  );

  const handleSave = useCallback(
    async (nameOverride) => {
      const payload = buildSavePayload(formData, {
        name: nameOverride ?? assessmentName,
        score: riskResult?.score,
      });

      const result = await dispatch(saveRiskAssessmentAsync(payload, riskResult?.id ?? selectedId));

      if (result === SUCCESS_STATUS) {
        hasManualSelectionRef.current = false;
        resetWorkspace();
      }
    },
    [dispatch, formData, assessmentName, riskResult, selectedId, resetWorkspace]
  );

  const handleOnSelect = useCallback(
    async (item) => {
      hasManualSelectionRef.current = true;
      setSelectedId(item?.id);

      const data = await dispatch(getRiskAssessmentByIdAsync(item?.id));

      if (!data) return;

      setFormData(mapAssessmentToFormData(data));
      setErrors({});
      setRiskResult({ score: data?.score });
      setAssessmentName(item?.name);
      setShowNameInput(false);
      setPendingName("");
      setView(VIEW.DETAIL);
    },
    [dispatch]
  );

  const handleEditRiskAssessment = useCallback(() => {
    setErrors({});
    setView(VIEW.FORM);
  }, []);

  const handleSaveRiskAssessment = useCallback(() => {
    if (!assessmentName) {
      setPendingName("");
      setShowNameInput(true);
      return;
    }
    handleSave();
  }, [assessmentName, handleSave]);

  const handleConfirmName = useCallback(() => {
    if (!pendingName) return;
    setAssessmentName(pendingName);
    setShowNameInput(false);
    handleSave(pendingName);
  }, [pendingName, handleSave]);

  const handleCancelName = useCallback(() => {
    setShowNameInput(false);
    setPendingName("");
  }, []);

  const handleEmailRiskAssessment = useCallback(async () => {
    const payload = { formularyId, isRecommendedProductScreen: 0 };
    const response = await dispatch(emailRecommendedProductAsync(payload, riskResult?.id));
    setSuccessMessage(response);
  }, [dispatch, formularyId, riskResult]);

  return {
    view,
    fields,
    formData,
    errors,
    score: riskResult?.score,
    assessmentName,
    selectedId,
    successMessage,
    showNameInput,
    pendingName,
    createRiskLoading,
    saveAssessmentLoading,
    emailLoading,
    detailLoading,
    riskLoading,
    riskAssessmentList,
    setPendingName,
    handleChipChange,
    handleCreate,
    handleCancelForm,
    onSubmit,
    handleOnSelect,
    handleEditRiskAssessment,
    handleSaveRiskAssessment,
    handleConfirmName,
    handleCancelName,
    handleEmailRiskAssessment,
  };
};
